import { useEffect, useMemo, useState } from "react";
import { Upload, Save } from "lucide-react";
import { fr } from "../../content/fr";
import { IMG } from "../../lib/images";
import { flattenContent, applyContent } from "../../lib/content";
import { SEO_ROUTES } from "../../lib/seo";
import { adminContent, adminUpdateContent, uploadProof, BACKEND, formatApiError } from "../../lib/api";

const SECTION_LABELS = {
  brand: "Marque", nav: "Navigation",
  hero: "Accueil · Hero", marquee: "Accueil · Bandeau", problem: "Accueil · Problème",
  howItWorks: "Accueil · Le raccourci", engine: "Accueil · Le Moteur", system: "Accueil · Pilote auto",
  pacte: "Accueil · PACTE", flow: "Accueil · Architecture", vsl: "Page VSL", offer: "Accueil · Offre",
  angles: "Accueil · Secteurs", specs: "Accueil · Spécifications", compliance: "Accueil · Conformité",
  transparency: "Accueil · Transparence", roi: "Accueil · Simulateur", faq: "Accueil · FAQ",
  qualification: "Accueil · Candidature", trust: "Accueil · Ancrage", finalCta: "Accueil · CTA final",
  footer: "Pied de page", consent: "Bannière cookies",
  pilote: "Page Pilote", commande: "Page Commande", merci: "Page Merci",
  challenge: "Page Challenge", partenaires: "Page Partenaires", ad: "Landing publicitaire",
  legal: "Pages légales",
};
const EXCLUDE = ["admin", "theme"];

function Field({ path, value, onChange }) {
  const label = path.split(".").slice(1).join(" · ") || path;
  const long = typeof value === "string" && (value.length > 70 || value.includes("\n"));
  return (
    <div className="field full" style={{ marginBottom: "0.9rem" }}>
      <label className="mono muted" style={{ fontSize: "0.7rem", textTransform: "uppercase" }}>{label}</label>
      {long ? (
        <textarea rows={3} value={value} onChange={(e) => onChange(path, e.target.value)} data-testid={`content-field-${path}`} />
      ) : (
        <input value={value} onChange={(e) => onChange(path, e.target.value)} data-testid={`content-field-${path}`} />
      )}
    </div>
  );
}

export default function ContentPanel() {
  const [sub, setSub] = useState("textes");
  const [values, setValues] = useState(null);
  const [images, setImages] = useState(null);
  const [seo, setSeo] = useState({});
  const [section, setSection] = useState(null);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const textFields = useMemo(() => flattenContent(fr, EXCLUDE), []);
  const imageFields = useMemo(() => flattenContent(IMG), []);

  useEffect(() => {
    adminContent().then((data) => {
      const tv = {};
      textFields.forEach((f) => { tv[f.path] = f.value; });
      setValues(tv);
      const iv = {};
      imageFields.forEach((f) => { iv[f.path] = f.value; });
      setImages(iv);
      setSeo(data.seo || {});
    }).catch(() => {});
  }, [textFields, imageFields]);

  const sections = useMemo(() => {
    const set = [];
    textFields.forEach((f) => { const s = f.path.split(".")[0]; if (!set.includes(s)) set.push(s); });
    return set;
  }, [textFields]);

  useEffect(() => { if (!section && sections.length) setSection(sections[0]); }, [sections, section]);

  const setText = (path, v) => setValues((s) => ({ ...s, [path]: v }));
  const setImg = (path, v) => setImages((s) => ({ ...s, [path]: v }));
  const setSeoField = (route, k, v) => setSeo((s) => ({ ...s, [route]: { ...(s[route] || {}), [k]: v } }));

  const onUpload = async (path, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingKey(path); setMsg(null);
    try { const r = await uploadProof(file); setImg(path, BACKEND + r.url); }
    catch { setMsg({ ok: false, t: "Échec de l'upload." }); }
    setUploadingKey(null);
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const cleanSeo = {};
      Object.entries(seo).forEach(([r, o]) => {
        const e = {};
        ["title", "description", "image"].forEach((k) => { if (o && o[k]) e[k] = o[k]; });
        if (Object.keys(e).length) cleanSeo[r] = e;
      });
      const data = await adminUpdateContent({ overrides: values, images, seo: cleanSeo });
      applyContent(data);
      setMsg({ ok: true, t: "Contenu enregistré. Les visiteurs verront les changements au prochain chargement." });
    } catch (e) { setMsg({ ok: false, t: formatApiError(e.response?.data?.detail) }); }
    setSaving(false);
  };

  if (!values || !images) return <p className="muted">Chargement…</p>;

  const subs = [["textes", "Textes"], ["images", "Images"], ["seo", "SEO"]];

  return (
    <div data-testid="admin-content">
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
        {subs.map(([k, l]) => (
          <button key={k} className={`tag ${sub === k ? "" : ""}`} onClick={() => setSub(k)} data-testid={`content-sub-${k}`}
            style={{ padding: "0.5rem 1rem", cursor: "pointer", background: sub === k ? "var(--brand-violet)" : "var(--grad-soft)", color: sub === k ? "#fff" : "var(--brand-violet)" }}>{l}</button>
        ))}
      </div>

      {sub === "textes" && (
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: "1.5rem", alignItems: "start" }}>
          <div className="glass" style={{ padding: "0.6rem", maxHeight: "70vh", overflowY: "auto" }} data-testid="content-sections">
            {sections.map((s) => (
              <button key={s} onClick={() => setSection(s)} data-testid={`content-section-${s}`}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "0.55rem 0.7rem", borderRadius: 8, marginBottom: 2, cursor: "pointer",
                  background: section === s ? "var(--grad-soft)" : "transparent", color: section === s ? "var(--brand-violet)" : "inherit",
                  fontWeight: section === s ? 600 : 400, border: "none", fontSize: "0.85rem" }}>
                {SECTION_LABELS[s] || s}
              </button>
            ))}
          </div>
          <div className="glass card">
            <h3 className="h3" style={{ marginBottom: "1.2rem" }}>{SECTION_LABELS[section] || section}</h3>
            {textFields.filter((f) => f.path.split(".")[0] === section).map((f) => (
              <Field key={f.path} path={f.path} value={values[f.path] ?? ""} onChange={setText} />
            ))}
          </div>
        </div>
      )}

      {sub === "images" && (
        <div className="glass card" data-testid="content-images">
          <h3 className="h3" style={{ marginBottom: "1.2rem" }}>Images du site</h3>
          {imageFields.map((f) => (
            <div key={f.path} style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1.1rem", flexWrap: "wrap" }}>
              <img src={images[f.path]} alt="" style={{ width: 84, height: 56, objectFit: "cover", borderRadius: 8, flexShrink: 0, border: "1px solid var(--border-subtle)" }} />
              <div style={{ flex: 1, minWidth: 220 }}>
                <label className="mono muted" style={{ fontSize: "0.7rem", textTransform: "uppercase" }}>{f.path}</label>
                <input value={images[f.path] ?? ""} onChange={(e) => setImg(f.path, e.target.value)} data-testid={`content-image-${f.path}`} />
              </div>
              <label className="btn btn--ghost" style={{ minHeight: 40, cursor: "pointer" }} data-testid={`content-image-upload-label-${f.path}`}>
                <Upload size={14} /> {uploadingKey === f.path ? "Envoi…" : "Remplacer"}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => onUpload(f.path, e)} data-testid={`content-image-upload-${f.path}`} />
              </label>
            </div>
          ))}
        </div>
      )}

      {sub === "seo" && (
        <div className="glass card" data-testid="content-seo">
          <h3 className="h3" style={{ marginBottom: "0.4rem" }}>Référencement (SEO) par page</h3>
          <p className="muted" style={{ fontSize: "0.85rem", marginBottom: "1.4rem" }}>Laissez vide pour utiliser le texte par défaut. Domaine : viralya.io</p>
          {SEO_ROUTES.map((r) => (
            <div key={r.key} className="glass" style={{ padding: "1rem", marginBottom: "1rem" }} data-testid={`seo-route-${r.key}`}>
              <div className="mono" style={{ color: "var(--brand-violet)", fontSize: "0.8rem", marginBottom: "0.6rem" }}>{r.label} · {r.key}</div>
              <div className="field full" style={{ marginBottom: "0.7rem" }}>
                <label>Titre (title)</label>
                <input value={seo[r.key]?.title || ""} onChange={(e) => setSeoField(r.key, "title", e.target.value)} data-testid={`seo-title-${r.key}`} />
              </div>
              <div className="field full" style={{ marginBottom: "0.7rem" }}>
                <label>Description (meta description)</label>
                <textarea rows={2} value={seo[r.key]?.description || ""} onChange={(e) => setSeoField(r.key, "description", e.target.value)} data-testid={`seo-desc-${r.key}`} />
              </div>
              <div className="field full">
                <label>Image de partage (og:image URL)</label>
                <input value={seo[r.key]?.image || ""} onChange={(e) => setSeoField(r.key, "image", e.target.value)} placeholder="https://…" data-testid={`seo-image-${r.key}`} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: "1.5rem", position: "sticky", bottom: 0, paddingTop: "0.5rem" }}>
        <button className="btn btn--primary" onClick={save} disabled={saving} data-testid="content-save"><Save size={15} /> {saving ? "Enregistrement…" : "Enregistrer le contenu"}</button>
        {msg && <p className={`form-status ${msg.ok ? "ok" : "err"}`} data-testid="content-msg">{msg.t}</p>}
      </div>
    </div>
  );
}
